import { OrderModalContent } from '../components/orders/order-modal-content'
import {
    convertDateYYYYMMDD,
    convertToCgiString,
    trimTrailingSpacesInArray,
} from '../utils'
import {
    GetBuildingFromInvoiceApiResponse,
    GetInvoiceSearchAPIResponse,
    GetOrderHeaderAPIResponse,
    GetOrderLinesAPIResponse,
    GetOrdersOrdFileAPIResponse,
    GetPendingOrderAPIResponse,
    GetPendingOrdersAPIResponse,
    GetShipToCodeApiResponse,
    InvoiceProrateObject,
    OrderType,
} from '../utils/interfaces/place-order.interface'
import { AbortController } from '../utils/useSentryQuery'
import { axiosInstance } from './api'
import { authQueryFn } from './auth'

export async function getOrders(payload: {
    startDate: string
    endDate: string
    type?: OrderType
}): Promise<GetPendingOrdersAPIResponse> {
    const { userDetails } = authQueryFn()
    const queryParam = new URLSearchParams({
        function: 'getpendingorders',
        alias: userDetails.api_config,
        branch: userDetails.Branch,
        company: userDetails.Company,
        properties: userDetails.Properties,
        startdate: payload.startDate,
        enddate: payload.endDate,
    })
    return axiosInstance('/api/getpendingorders', {
        params: queryParam,
    }).then((response) => response.data as GetPendingOrdersAPIResponse)
}

export async function getSalesAnalysis({
    startEndDate,
    signal,
    ...params
}: {
    startEndDate: { from: Date; to: Date }
    buildings?: string
    signal: AbortController
    units?: string
}) {
    const { userDetails } = authQueryFn()
    const queryParam = new URLSearchParams({
        function: 'getsalesanalysis',
        alias: userDetails.api_config,
        branch: userDetails?.Branches || userDetails.Branch,
        company: userDetails.Company,
        properties: userDetails.Properties,
        startdate: convertDateYYYYMMDD(startEndDate.from),
        enddate: convertDateYYYYMMDD(startEndDate.to),
        ...(params ?? {}),
    })
    return axiosInstance.get('/api/getsalesanalysis', {
        params: queryParam,
        signal,
    })
}

export async function getShipToCodes({ signal }: { signal: AbortController }) {
    const { userDetails } = authQueryFn()
    return axiosInstance
        .get('/api/testdebug', {
            params: {
                function: 'shiptocodes',
                branch: userDetails?.Branches || userDetails.Branch,
                company: userDetails.Company,
                properties: userDetails.Properties,
                alias: userDetails.api_config,
            },
            signal,
        })
        .then((resp) => resp.data as GetShipToCodeApiResponse)
}

export async function getBuildingFromInvoice({
    signal,
}: {
    signal: AbortController
}) {
    const { userDetails } = authQueryFn()
    return axiosInstance
        .get('/api/bldgfrominvoice', {
            params: {
                function: 'bldgfrominvoice',
                branch: userDetails?.Branches || userDetails.Branch,
                company: userDetails.Company,
                properties: userDetails.Properties,
                alias: userDetails.api_config,
            },
            signal,
        })
        .then((resp) => resp.data as GetBuildingFromInvoiceApiResponse)
}

// /api/v2/testdebug?function=getordersordfile
// &alias=sales_demo
// &branch=1/*%5C2/*%5C3
// &company=99
// &properties=JWOODS%2c0000/*%5CPATRICK%2c0134/*%5CPATRICK%2c1728/*%5CPATRICK%2c0000/*%5CALLS6108%2c0000/*%5COTIL2034%2c0000/*%5CPRITCH02%2c0000/*%5CPRITCHET%2c1743/*%5CBIGDADDY%2c0000/*%5CMANDALAY%2c0000/*%5CSURFACES%2c0000/*%5C
// &startdate=20220101
// &enddate=20230101

// /api/v2/testdebug?function=getordersordfile
// &alias=sales_demo
// &branch=1/*%5C2/*%5C3
// &company=99
// &properties=MANDALAY,0000
// &buildings=3960//**%5C%5C3965
// &units=103//**%5C%5C11124
// &startdate=20220101
// &enddate=20230101

export async function getOrdersOrdFile(payload: {
    startDate: Date
    endDate: Date
    status: string
    buildings?: string[]
    units?: string[]
    properties?: string[]
    signal: AbortSignal
}): Promise<{
    pending: GetPendingOrdersAPIResponse
    installed: GetOrdersOrdFileAPIResponse
}> {
    const { userDetails } = authQueryFn()
    let endpoint = []
    switch (payload.status) {
        case 'all':
            endpoint = ['getpendingorders', 'getordersordfile']
            break
        case 'pending':
            endpoint = ['getpendingorders']
            break
        case 'installed':
            endpoint = ['getordersordfile']
            break
        default:
            endpoint = ['getpendingorders']
            break
    }

    const fetchOrdersPromises = endpoint.map((endpnt) => {
        const params = {
            function: endpnt,
            branch: userDetails?.Branches || userDetails.Branch,
            company: userDetails.Company,
            // For properties, the separator remains /*\
            properties: payload?.properties?.length
                ? payload?.properties?.join('/*\\')
                : userDetails.Properties,
            // buildings: separator /**\\  3960/**\\3965
            buildings: convertToCgiString(payload?.buildings || []),
            // units: separator /**\\ 103//**\\11124
            units: convertToCgiString(payload?.units || []),
            alias: userDetails.api_config,
            startdate: convertDateYYYYMMDD(payload.startDate),
            enddate: convertDateYYYYMMDD(payload.endDate),
        }
        return axiosInstance.get(`/api/${endpnt}`, {
            params,
            signal: payload.signal,
        })
    })
    const results = await Promise.all(fetchOrdersPromises)

    switch (payload.status) {
        case 'all':
            return {
                pending: trimTrailingSpacesInArray(
                    results[0].data
                ) as GetPendingOrdersAPIResponse,
                installed: trimTrailingSpacesInArray(
                    results[1].data
                ) as GetOrdersOrdFileAPIResponse,
            }
        case 'installed':
            return {
                pending: [],
                installed: trimTrailingSpacesInArray(
                    results[0].data
                ) as GetOrdersOrdFileAPIResponse,
            }
        case 'pending':
            return {
                installed: [],
                pending: trimTrailingSpacesInArray(
                    results[0].data
                ) as GetPendingOrdersAPIResponse,
            }
    }
    return {
        pending: [],
        installed: [],
    }
}

export async function getPendingOrder({
    orderId,
    branch,
    properties,
    signal,
}: {
    orderId: string
    branch?: string
    properties?: string
    signal: AbortController
}) {
    const { userDetails } = authQueryFn()
    return axiosInstance
        .get('/api/getpendingorder', {
            params: {
                function: 'getpendingorder',
                branch: ![undefined, 'undefined'].includes(branch)
                    ? branch
                    : userDetails?.Branches || userDetails.Branch,
                company: userDetails.Company,
                properties: ![undefined, 'undefined'].includes(properties)
                    ? properties
                    : userDetails.Properties,
                alias: userDetails.api_config,
                transaction: orderId,
            },
            signal,
        })
        .then((resp) => resp.data as GetPendingOrderAPIResponse)
        .catch(() => {})
}

// Pending Orders

// /api/v2/testdebug?function=getpendingorders
// &alias=sales_demo
// &branch=1/*%5C2/*%5C3
// &company=99
// &properties=MANDALAY,0000
// &buildings=3960
// &units=645//**%5C%5C23
// &startdate=20220101
// &enddate=20230101

// /api/v2/testdebug?function=getinvoicessearch
// &alias=sales_demo
// &branch=1/*%5C2/*%5C3
// &company=99
// &properties=JWOODS%2c0000/*%5CPATRICK%2c0134/*%5CPATRICK%2c1728/*%5CPATRICK%2c0000/*%5CALLS6108%2c0000/*%5COTIL2034%2c0000/*%5CPRITCH02%2c0000/*%5CPRITCHET%2c1743/*%5CBIGDADDY%2c0000/*%5CMANDALAY%2c0000/*%5CSURFACES%2c0000/*%5C
// &searchin=DMH_SHADDR1
// &searchtype=equals
// &searchfor=
// &searchforno=
// &startdate=20231202
// &enddate=20241202
// &proptypes=MANDALAY%2f*%5c0000%2f*%5c1BEDROOM+SPECIAL%2f*%5c1%2f%2f**%5c%5cMANDALAY%2f*%5c0000%2f*%5cB4%2f*%5c1%2f%2f**%5c%5c
// MANDALAY/*\0000/*\1BEDROOM+SPECIAL/*\1
// //**\\
// MANDALAY/*\0000/*\B4/*\1//**\\

// B4/*\1
// PDMO_PROPTYPE/*\PDMO_WHSE

export async function getInvoiceSearch({
    signal,
    ...payload
}: {
    signal: AbortController
    startDate: Date
    endDate: Date
    searchin?: string
    searchforno?: string
    searchfor?: string
    status?: string
    properties?: string[]
    buildings?: string[]
    units?: string[]
    searchtype: string
    unitTypes?: string[] // ["B4,1"]
    shipTos?: string[] // These are MANDALAY,0000, customer and shipTo Combined. ["MANDALAY,0000"]
}): Promise<GetInvoiceSearchAPIResponse> {
    const { userDetails } = authQueryFn()
    const finalPropTypes: string[] = []

    if (payload?.shipTos) {
        // payload.properties
        payload?.shipTos?.map((eachLocation) => {
            payload.unitTypes?.map((eachProperty) => {
                finalPropTypes.push(
                    eachLocation.replaceAll(',', '/*\\') +
                        '/*\\' +
                        eachProperty
                            .trim()
                            .replaceAll(' ', '+')
                            .replaceAll(',', '/*\\')
                )
            })
        })
    }
    if (payload?.status === 'all') {
        //TODO  : if I pass it getting the more data
        delete payload.status
    }
    if (!payload?.searchfor) {
        //TODO  : if I pass it getting the more data
        delete payload.searchfor
        delete payload.searchtype
        delete payload.searchin
    }

    return axiosInstance
        .get('/api/getinvoicessearch', {
            params: {
                function: 'getinvoicessearch',
                branch: userDetails?.Branches || userDetails.Branch,
                company: userDetails.Company,
                // For properties, the separator remains /*\
                properties:
                    payload?.properties?.join('/*\\') ?? userDetails.Properties,
                proptypes: convertToCgiString(finalPropTypes),
                // buildings: separator /**\\  3960/**\\3965
                buildings: convertToCgiString(payload?.buildings || []) || null,
                // units: separator /**\\ 103//**\\11124
                units: convertToCgiString(payload?.units || []) || null,
                searchfor: payload?.searchfor,
                searchin: payload.searchin,
                status: payload.status,
                searchtype: payload.searchtype,
                searchforno: payload?.searchfor,
                alias: userDetails.api_config,
                startdate: convertDateYYYYMMDD(payload.startDate),
                enddate: convertDateYYYYMMDD(payload.endDate),
            },
            signal,
        })
        .then(
            (resp) =>
                trimTrailingSpacesInArray(
                    resp.data
                ) as GetInvoiceSearchAPIResponse
        )
}

export async function getInvoiceProrate({
    invoiceNo,
    signal,
}: {
    invoiceNo: string
    signal: AbortController
}): Promise<InvoiceProrateObject[]> {
    const { userDetails } = authQueryFn()

    return axiosInstance.get('/api/testdebug', {
        params: {
            function: 'getinvoicesreport',
            branch: userDetails.Branch,
            company: userDetails.Company,
            properties: userDetails.Properties,
            alias: userDetails.api_config,
            invoices: `${invoiceNo},${userDetails.Branch}`,
        },
        signal,
    })
}

export async function getOrderHeader({
    order,
    branch,
    properties,
    signal,
}: {
    order: string
    branch: string
    properties: string
    signal: AbortController
}) {
    const { userDetails } = authQueryFn()
    return axiosInstance
        .get('/api/order', {
            params: {
                function: 'getorderheader',
                branch: branch,
                company: userDetails.Company,
                properties: properties.includes('undefined')
                    ? userDetails.Properties
                    : properties,
                alias: userDetails.api_config,
                order,
            },
            signal,
        })
        .then(
            (resp) =>
                trimTrailingSpacesInArray(
                    resp.data
                )?.[0] as GetOrderHeaderAPIResponse[0]
        )
}

export async function getOrderLines({
    orderRow,
    signal,
}: {
    orderRow: Parameters<typeof OrderModalContent>[0]['orderRow']
    signal: AbortController
}): Promise<GetOrderLinesAPIResponse> {
    const { userDetails } = authQueryFn()
    const branch = orderRow?.BRANCH || orderRow?.DMO_WHSE || orderRow.PODMO_WHSE
    const properties =
        orderRow.shipTo ||
        (orderRow?.PODMO_CUSTID
            ? `${orderRow?.PODMO_CUSTID},${orderRow?.PODMO_SHCODE}`
            : `${orderRow?.DMH_CUSTID},${orderRow?.['DMH-SHCODE'] || orderRow?.DMH_SHCODE}`)
    return axiosInstance
        .get('/api/getorderlines', {
            params: {
                function: 'getorderlines',
                branch: branch || userDetails?.Branches || userDetails.Branch,
                company: userDetails.Company,
                properties: !properties.includes('undefined')
                    ? properties
                    : userDetails.Properties,
                alias: userDetails.api_config,
                order: orderRow.id,
            },
            signal,
        })
        .then((resp) =>
            trimTrailingSpacesInArray<GetOrderLinesAPIResponse>(resp.data)
        )
}
